import api from '../utils/api';

const BASE = '/api/notifications';

/**
 * Get the authenticated user's notifications.
 * @route GET /api/notifications/get-notifications
 * @returns {Promise<{ notifications: Object[] }>}
 */
export const getNotifications = () =>
    api.get(`${BASE}/get-notifications`).then((r) => r.data);

/**
 * Mark a single notification as seen.
 * @route PATCH /api/notifications/markNotificationAsSeen/:userId/:notificationId
 * @param {string} userId
 * @param {string} notificationId
 * @returns {Promise<string>} Status text
 */
export const markNotificationAsSeen = (userId, notificationId) =>
    api.patch(`${BASE}/markNotificationAsSeen/${userId}/${notificationId}`).then((r) => r.data);

/**
 * Mark every unseen announcement notification for one classroom as seen.
 * @route PATCH /api/notifications/markClassroomAnnouncementsAsSeen/:classroomId
 * @param {string} classroomId
 * @returns {Promise<{ notifications: Object[] }>} The user's updated notifications
 */
export const markClassroomAnnouncementsAsSeen = (classroomId) =>
    api.patch(`${BASE}/markClassroomAnnouncementsAsSeen/${classroomId}`).then((r) => r.data);

/**
 * Accept a classroom invite.
 * @route PATCH /api/notifications/acceptInvite
 * @param {string} relatedClassroomId
 * @param {string} role - 'student' or 'teacher'
 * @param {string} notificationId - the invite notification to consume
 * @returns {Promise<string>} Status text
 */
export const acceptInvite = (relatedClassroomId, role, notificationId) =>
    api.patch(`${BASE}/acceptInvite`, { relatedClassroomId, role, notificationId }).then((r) => r.data);

/**
 * Dismiss a classroom invite.
 * @route PATCH /api/notifications/ignoreInvite
 * @param {string} notificationId
 * @returns {Promise<string>} Status text
 */
export const ignoreInvite = (notificationId) =>
    api.patch(`${BASE}/ignoreInvite`, { notificationId }).then((r) => r.data);

/**
 * Validate an emailed invite token.
 * @route GET /api/notifications/verifyInviteToken
 * @param {string} token - signed JWT invite token
 * @returns {Promise<{ valid: boolean, classroomId?: string }>}
 */
export const verifyInviteToken = (token) =>
    api.get(`${BASE}/verifyInviteToken`, { params: { token } }).then((r) => r.data);

/**
 * Accept a classroom invite via an emailed token.
 * @route PATCH /api/notifications/acceptInviteFromLink
 * @param {string} token - signed JWT invite token
 * @returns {Promise<string>} Status text
 */
export const acceptInviteFromLink = (token) =>
    api.patch(`${BASE}/acceptInviteFromLink`, { token }).then((r) => r.data);

/**
 * Join a classroom using its join code.
 * @route POST /api/notifications/joinClassroomFromCode
 * @param {string} classroomCode
 * @returns {Promise<{ message: string, classroom: Object }>}
 */
export const joinClassroomFromCode = (classroomCode) =>
    api.post(`${BASE}/joinClassroomFromCode`, { classroomCode }).then((r) => r.data);

/**
 * Invite a user to a classroom (push notification + emailed invite link).
 * @route POST /api/notifications/sendInvite
 * @param {string} invitedUserId
 * @param {string} classroomId
 * @param {string} name - classroom name shown in the notification/email
 * @param {string} role - 'student' or 'teacher'
 * @returns {Promise<string>} Status text
 */
export const sendInvite = (invitedUserId, classroomId, name, role) =>
    api.post(`${BASE}/sendInvite`, { invitedUserId, classroomId, name, role }).then((r) => r.data);

/**
 * Send a classroom announcement to its students (push + email).
 * @route POST /api/notifications/sendAnnouncementToStudents
 * @param {Array<{ _id: string, email: string }>} students
 * @param {string} classroomId
 * @param {string} classroomName
 * @param {string} announcementText - rich-text/HTML body
 * @returns {Promise<string>} Status text
 */
export const sendAnnouncementToStudents = (students, classroomId, classroomName, announcementText) =>
    api.post(`${BASE}/sendAnnouncementToStudents`, { students, classroomId, classroomName, announcementText }).then((r) => r.data);

/**
 * Notify students of a new assignment (push + email).
 * @route POST /api/notifications/sendAssignmentToStudents
 * @param {Array<{ _id: string }>} assignees - only `_id` is read; addresses are resolved server-side
 * @param {string} classroomId
 * @param {string} classroomName
 * @param {string} assignmentTitle
 * @returns {Promise<string>} Status text
 */
export const sendAssignmentToStudents = (assignees, classroomId, classroomName, assignmentTitle) =>
    api.post(`${BASE}/sendAssignmentToStudents`, { assignees, classroomId, classroomName, assignmentTitle }).then((r) => r.data);

/**
 * Notify the other party of a new message on a feedback thread (push + email).
 * @route POST /api/notifications/sendMessageNotification/:userId/:feedbackId
 * @param {string} recipientId - originally targeted user; actual recipient is derived from the thread
 * @param {string} feedbackId
 * @param {Object} payload - { messageSenderId, messageText, classroomId?, classroomName?, assignmentTitle? }
 * @returns {Promise<string>} Status text
 */
export const sendMessageNotification = (recipientId, feedbackId, payload) =>
    api.post(`${BASE}/sendMessageNotification/${recipientId}/${feedbackId}`, payload).then((r) => r.data);

/**
 * Notify a user that they have new feedback (push + email).
 * @route POST /api/notifications/sendFeedbackNotification/:userId
 * @param {string} creatorId - recipient User ObjectId
 * @param {Object} payload - { feedbackText, assignmentId? }
 * @returns {Promise<string>} Status text
 */
export const sendFeedbackNotification = (creatorId, payload) =>
    api.post(`${BASE}/sendFeedbackNotification/${creatorId}`, payload).then((r) => r.data);

/**
 * Notify a user that an admin responded to their site feedback (push + email).
 * @route POST /api/notifications/sendSiteFeedback
 * @param {{ _id: string, email: string, username: string }} userData
 * @returns {Promise<string>} Status text
 */
export const sendSiteFeedback = (userData) =>
    api.post(`${BASE}/sendSiteFeedback`, { userData }).then((r) => r.data);
